import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, AlertCircle, Camera, Image as ImageIcon, X, Loader2, Phone, Mail, MessageSquare } from 'lucide-react';
import { parseTradeInfo, ParsedTradeInfo } from './parseTradeInfo';
import { insertTradesperson } from '@/lib/supabase/trades';
import { addUserTradesperson, searchTradeByContact, isTradesPersonSavedByUser } from '@/lib/supabase/trades';
import { ensureFreshAuth } from '@/lib/supabase/auth';
import { Tradesperson } from '@/lib/types';
import { debug } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { performOCR } from './imageUtils';
import { getTradeIconForTrade } from '@/lib/tradeUtils';
import { TradeIcon } from '@/components/ui/trade-icon';
import { TradePersonCard } from '@/components/TradePersonCard';
import { TRADE_CATEGORIES } from '@/lib/tradeCategories';

interface QuickAddTradeProps {
  onSuccess?: () => void;
  userPostcode?: string;
  onInputChange?: (isActive: boolean) => void;
}

export function QuickAddTrade({ onSuccess, userPostcode, onInputChange }: QuickAddTradeProps) {
  const { user } = useAuth();
  const userId = user?.id;

  // iOS detection for PWA - more comprehensive than just user agent
  const isIOSDevice = (() => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const hasTouch = 'ontouchstart' in window;

    // If it's iOS, or looks like iOS PWA, disable camera
    return isIOS || (isPWA && hasTouch && /Safari/.test(ua));
  })();

  // Input state
  const [inputText, setInputText] = useState('');
  const debouncedInputText = useDebounce(inputText, 500); // 500ms debounce

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Progressive disclosure state
  const [isExpanded, setIsExpanded] = useState(false);

  // Display mode state - controls what UI is shown
  type DisplayMode = 'form' | 'alreadySaved' | 'existingRecord';
  const [displayMode, setDisplayMode] = useState<DisplayMode>('form');

  // Parsing state
  const [isParsing, setIsParsing] = useState(false);
  const [parsedInfo, setParsedInfo] = useState<ParsedTradeInfo>({
    name: '',
    trade_type: '',
    phone: '',
    email: '',
    postcode: userPostcode || '',
    detectedFields: []
  });

  // Photo state
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  // Editable fields (what the user can modify)
  const [editableFields, setEditableFields] = useState({
    name: '',
    trade_type: '',
    phone: '',
    email: '',
    postcode: userPostcode || '',
    notes: '', // Adding notes field
  });

  // Duplicate detection
  const [possibleMatch, setPossibleMatch] = useState<Tradesperson | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExistingRecord, setIsExistingRecord] = useState(false);
  const [isAlreadySavedByUser, setIsAlreadySavedByUser] = useState(false);

  // Define option type with optional disabled property
  interface TradeOption {
    value: string;
    label: string;
    disabled?: boolean;
  }

  // Define most common trade types (sorted by popularity)
  const POPULAR_TRADE_IDS = [
    'plumber',
    'electrician',
    'heating engineer',
    'builder',
    'decorator',
    'gardener',
    'cleaner',
    'roofer'
  ];

  // Group trade categories by UI category for the dropdown
  const getTradeOptions = (): TradeOption[] => {
    // Get all valid trade categories
    const validTrades = TRADE_CATEGORIES.filter(category => !category.isLegacy);

    // Separate popular and other trades
    const popularTrades = validTrades.filter(trade => POPULAR_TRADE_IDS.includes(trade.id));
    const otherTrades = validTrades.filter(trade => !POPULAR_TRADE_IDS.includes(trade.id));

    // Sort popular trades by their popularity order
    popularTrades.sort((a, b) => POPULAR_TRADE_IDS.indexOf(a.id) - POPULAR_TRADE_IDS.indexOf(b.id));

    // Sort other trades alphabetically
    otherTrades.sort((a, b) => a.displayName.localeCompare(b.displayName));

    // Get unique UI categories from remaining trades
    const uiCategories = Array.from(
      new Set(
        otherTrades.map(category => category.uiCategory)
      )
    ).sort();

    // Start with empty option
    const options: TradeOption[] = [{ value: '', label: 'Select trade type...' }];

    // Add popular trades section
    if (popularTrades.length > 0) {
      options.push({
        value: 'group-popular',
        label: '--- Most Common ---',
        disabled: true
      });

      popularTrades.forEach(trade => {
        options.push({
          value: trade.id,
          label: trade.displayName
        });
      });
    }

    // Add trades grouped by UI category
    uiCategories.forEach(uiCategory => {
      if (!uiCategory) return;

      // Get trades for this UI category (excluding popular ones)
      const categoryTrades = otherTrades
        .filter(trade => trade.uiCategory === uiCategory)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      if (categoryTrades.length === 0) return;

      // Add option group label
      options.push({
        value: `group-${uiCategory}`,
        label: `--- ${uiCategory.charAt(0).toUpperCase() + uiCategory.slice(1)} ---`,
        disabled: true
      });

      // Add trades in this category
      categoryTrades.forEach(trade => {
        options.push({
          value: trade.id,
          label: trade.displayName
        });
      });
    });

    return options;
  };

  // Available trade types for the dropdown
  const tradeOptions = getTradeOptions();

  // Handle photo capturing and processing
  const handleCapturePhoto = async (file: File) => {
    try {
      // Validate file is an image
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Set the photo and create preview URL immediately
      // This ensures the UI shows the photo even if OCR fails
      setPhoto(file);
      setPhotoPreviewUrl(URL.createObjectURL(file));
      setIsProcessingImage(true);
      setIsExpanded(true); // Always expand to allow manual entry
      onInputChange?.(true);

      // Initialize the form fields so the user can start entering data
      // even before OCR completes
      setEditableFields(prev => ({
        ...prev,
        name: prev.name,
        trade_type: prev.trade_type,
        phone: prev.phone,
        email: prev.email,
        postcode: userPostcode || prev.postcode || '',
        notes: prev.notes,
      }));

      debug.log('Starting OCR processing for file:', file.name, file.type, file.size);

      // Check if iOS and inform user
      const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOSDevice) {
        toast.info('Photo saved! Please enter the details from the image below.');
      }

      // Process the image with OCR in the background
      try {
        const extractedText = await performOCR(file);
        debug.log('Extracted text from image:', extractedText);

        if (!extractedText || extractedText.trim().length === 0) {
          // Check if this is iOS where OCR is disabled
          const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
          if (isIOSDevice) {
            toast.info('Please enter the tradesperson details from the photo below.');
          } else {
            toast.warning('No text was detected in the image. Try a clearer photo or enter details manually.');
          }
        } else {
          // Set the extracted text in the input field
          setInputText(extractedText);

          // Make sure form stays expanded after text is set
          setIsExpanded(true);
          onInputChange?.(true);
        }
      } catch (ocrError) {
        debug.error('OCR processing error:', ocrError);

        // More specific error messages based on error type
        if (ocrError.message && ocrError.message.includes('timed out')) {
          toast.error('Image processing timed out. Please enter details manually.');
        } else if (ocrError.message && ocrError.message.includes('text recognition engine')) {
          toast.error('Could not load text recognition engine. Please try again later.');
        } else {
          toast.error('Could not process image. Try using a clearer photo or manually enter the details.');
        }

        // Continue with empty text so the user can enter information manually
        toast.info('You can still enter the tradesperson details manually below.');
      }

      setIsProcessingImage(false);
    } catch (error) {
      debug.error('Error handling photo capture:', error);
      toast.error('Failed to process image. Please try again or enter details manually.');
      setIsProcessingImage(false);

      // Ensure UI remains in a usable state even after errors
      setIsExpanded(true);
      onInputChange?.(true);
    }
  };

  // Remove the photo and reset related state
  const handleRemovePhoto = () => {
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhoto(null);
    setPhotoPreviewUrl(null);
  };

  // Parse the input text whenever it changes (debounced)
  useEffect(() => {
    const parseInput = async () => {
      if (!debouncedInputText) {
        // Clear parsed info if input is empty
        setParsedInfo({
          name: '',
          trade_type: '',
          phone: '',
          email: '',
          postcode: userPostcode || '',
          detectedFields: []
        });

        // Reset duplicate detection state
        setPossibleMatch(null);
        setIsExistingRecord(false);
        setIsAlreadySavedByUser(false);
        setDisplayMode('form');

        // Reset form fields to blank state
        setEditableFields({
          name: '',
          trade_type: '',
          phone: '',
          email: '',
          postcode: userPostcode || '',
          notes: '',
        });

        // Collapse the component
        setIsExpanded(false);
        onInputChange?.(false);
        return;
      }

      // If user has started typing, expand the component
      setIsExpanded(true);
      onInputChange?.(true);
      setIsParsing(true);

      try {
        // Parse the text
        const parsed = parseTradeInfo(debouncedInputText);
        setParsedInfo(parsed);

        // Update editable fields with parsed values, but don't override user-edited values
        // Always keep the user's postcode, don't use detected postcodes
        setEditableFields(prev => ({
          name: parsed.name || prev.name,
          trade_type: parsed.trade_type || prev.trade_type,
          phone: parsed.phone || prev.phone,
          email: parsed.email || prev.email,
          postcode: userPostcode || prev.postcode || '',
          notes: prev.notes, // Preserve any existing notes
        }));

        // Check for duplicates if we have a phone or email
        if (parsed.phone || parsed.email) {
          debug.log("QuickAddTrade: Checking for duplicate tradesperson:", {
            phone: parsed.phone,
            email: parsed.email
          });

          const duplicate = await searchTradeByContact(parsed.phone, parsed.email);

          debug.log("QuickAddTrade: Search result:", duplicate);

          // If we find a match, fill the form with the existing data
          if (duplicate) {
            debug.log("QuickAddTrade: Match found! Showing existing tradesperson:", duplicate);

            // Store the match and mark as existing record
            setPossibleMatch(duplicate);
            setIsExistingRecord(true);

            // Check if this tradesperson is already saved by the user
            if (userId) {
              try {
                const isSaved = await isTradesPersonSavedByUser(userId, duplicate.id);
                debug.log("QuickAddTrade: Is tradesperson already saved by user:", isSaved);
                setIsAlreadySavedByUser(isSaved);

                // Update the display mode based on whether it's already saved or just in the database
                if (isSaved) {
                  setDisplayMode('alreadySaved');
                } else {
                  setDisplayMode('existingRecord');
                }
              } catch (error) {
                debug.error("QuickAddTrade: Error checking if tradesperson is saved:", error);
                setIsAlreadySavedByUser(false);
                setDisplayMode('existingRecord');
              }
            } else {
              // If no user ID, just show existingRecord view
              setDisplayMode('existingRecord');
            }

            // Pre-fill form with the matched tradesperson's data
            // Get current notes to preserve them
            const currentNotes = editableFields.notes;

            // Debug any nulls in the duplicate data
            if (!duplicate.org && !duplicate.contact) {
              debug.warn("QuickAddTrade: Duplicate has no name data (org or contact)");
            }

            if (!duplicate.trade_type && !duplicate.category) {
              debug.warn("QuickAddTrade: Duplicate has no trade type data");
            }

            // Pre-fill form with the matched tradesperson's data - explicitly force values
            const existingData = {
              name: duplicate.org || duplicate.contact || "",
              trade_type: duplicate.trade_type || duplicate.category || "",
              phone: duplicate.phone || "",
              email: duplicate.email || "",
              postcode: userPostcode || "",
              notes: currentNotes // Preserve any existing notes
            };

            debug.log("QuickAddTrade: Pre-filling form with:", existingData);

            // Update form fields with the existing data
            setEditableFields(existingData);
          } else {
            debug.log("QuickAddTrade: No match found");
            setPossibleMatch(null);
            setIsExistingRecord(false);
            setIsAlreadySavedByUser(false);
            setDisplayMode('form');
          }
        }
      } catch (error) {
        debug.error('Error parsing trade info:', error);
      } finally {
        setIsParsing(false);
      }
    };

    parseInput();
  }, [debouncedInputText, userPostcode, onInputChange, userId]);

  // Update a single editable field
  const updateField = (field: keyof typeof editableFields, value: string) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Use existing tradesperson
  const handleUseExisting = async () => {
    if (!possibleMatch || !userId) return;

    setIsSubmitting(true);

    try {
      // Ensure fresh auth before database operation
      await ensureFreshAuth();

      await addUserTradesperson(userId, possibleMatch.id, {
        preferred: false,
        notes: editableFields.notes ?
          editableFields.notes :
          `Added via quick entry on ${new Date().toLocaleDateString()}`
      });

      toast.success('Tradesperson added to your list!');
      resetForm();
      onSuccess?.();
    } catch (error) {
      debug.error('Error adding existing tradesperson:', error);
      toast.error('Failed to add tradesperson to your list');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Continue with new tradesperson - preserving phone/email
  const handleContinueWithNew = () => {
    // Store current values we want to keep
    const currentPhone = editableFields.phone;
    const currentEmail = editableFields.email;

    setPossibleMatch(null);
    setIsExistingRecord(false);
    setDisplayMode('form');

    // Clear form fields but keep the detected phone/email that triggered the match
    setEditableFields({
      name: '',
      trade_type: '',
      phone: currentPhone, // Keep current phone number
      email: currentEmail, // Keep current email
      postcode: userPostcode || '',
      notes: ''
    });

    debug.log("User chose to create new record - cleared form but kept contact info:", {
      phone: currentPhone,
      email: currentEmail
    });
  };

  // Save new tradesperson
  const handleSave = async () => {
    if (!userId) {
      toast.error('You must be logged in to add tradespeople');
      return;
    }

    // Validate minimum required fields
    if (!editableFields.name) {
      toast.error('Please provide a name for the tradesperson');
      return;
    }

    if (!editableFields.trade_type) {
      toast.error('Please select a trade type');
      return;
    }

    setIsSubmitting(true);

    try {
      // DEBUG: Check what we actually have
      debug.log('🔍 QUICKADD DEBUG - handleSave called');
      debug.log('🔍 user object:', user);
      debug.log('🔍 userId:', userId);
      debug.log('🔍 userPostcode:', userPostcode);

      // Ensure fresh auth before database operations
      await ensureFreshAuth();

      // Insert new tradesperson
      const result = await insertTradesperson({
        contact: editableFields.name,
        org: editableFields.name, // Use name for organization as well for MVP
        category: editableFields.trade_type,
        trade_type: editableFields.trade_type,
        phone: editableFields.phone,
        email: editableFields.email,
        postcode: userPostcode || '', // Always use the user's postcode
        verified: false,
        created_by: userId,
      });

      if (!result.data || result.data.length === 0) {
        throw new Error('Failed to create tradesperson');
      }

      // Ensure fresh auth again before second database operation
      await ensureFreshAuth();

      // Add to user's list
      await addUserTradesperson(userId, result.data[0].id, {
        preferred: false,
        notes: editableFields.notes ?
          editableFields.notes :
          `Added via quick entry on ${new Date().toLocaleDateString()}`
      });

      toast.success('Tradesperson added successfully!');
      resetForm();
      onSuccess?.();
    } catch (error) {
      debug.error('Error saving tradesperson:', error);
      toast.error('Failed to save tradesperson');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle the "Start Over" action
  const handleStartOver = () => {
    resetForm();
  };

  // Reset the form
  const resetForm = () => {
    setInputText('');
    setParsedInfo({
      name: '',
      trade_type: '',
      phone: '',
      email: '',
      postcode: userPostcode || '',
      detectedFields: []
    });
    setEditableFields({
      name: '',
      trade_type: '',
      phone: '',
      email: '',
      postcode: userPostcode || '', // Keep this in the state for data consistency
      notes: '', // Clear notes field
    });
    setPossibleMatch(null);
    setIsExistingRecord(false);
    setIsAlreadySavedByUser(false);
    setDisplayMode('form');

    // Clear photo state
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhoto(null);
    setPhotoPreviewUrl(null);
  };

  // Render the already saved comparison view
  const renderAlreadySavedView = () => {
    if (!possibleMatch) return null;

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-5 shadow-sm">
        <div className="flex justify-center items-center gap-2 mb-4 text-green-700">
          <Check className="h-5 w-5" />
          <h3 className="text-lg font-medium">This contact is already in your list!</h3>
        </div>

        {/* Photo preview if available */}
        {photoPreviewUrl && (
          <div className="mb-5 border-2 border-purple-200 rounded-lg overflow-hidden shadow-sm bg-white">
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 font-medium border-b">
              Your Photo
            </div>
            <div className="p-2">
              <img
                src={photoPreviewUrl}
                alt="Business card"
                className="w-full max-h-[180px] object-contain"
              />
            </div>
          </div>
        )}

        {/* Input preview if no photo */}
        {!photoPreviewUrl && inputText && (
          <div className="mb-5 border-2 border-purple-200 rounded-lg overflow-hidden shadow-sm bg-white">
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 font-medium border-b">
              Your Input
            </div>
            <div className="p-4 text-gray-800 font-mono text-sm whitespace-pre-wrap">
              {inputText}
            </div>
          </div>
        )}

        {/* Saved trade card */}
        <div className="mb-5 border-2 border-purple-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 font-medium border-b">
            From Your List
          </div>
          <div className="p-2">
            {/* Import and use SavedTradeCard component */}
            <div className="bg-white">
              <TradePersonCard trade={possibleMatch} mode="saved" username={undefined} />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-4 mt-6">
          <Button
            onClick={() => {
              resetForm();
              onSuccess?.(); // This will typically navigate to the list view
            }}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg text-base font-medium shadow-sm"
          >
            Show in My List
          </Button>
          <Button
            onClick={handleStartOver}
            variant="outline"
            className="flex-1 border-gray-300 text-gray-700 py-3 rounded-lg text-base font-medium"
          >
            Start Over
          </Button>
        </div>
      </div>
    );
  };

  // Render the form view for adding tradespeople
  const renderFormView = () => {
    return (
      <>
        <div className="mb-4">
          {isExpanded && (
            <div className="mb-2">
              <label htmlFor="trade-info" className="block text-xs font-medium text-purple-800 opacity-50">
                Original text:
              </label>
            </div>
          )}

          {/* Photo Preview */}
          {photoPreviewUrl && (
            <div className="relative mb-4 border-2 border-purple-200 rounded-lg overflow-hidden shadow-sm">
              <div className="absolute top-3 right-3 z-10">
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="bg-white rounded-full p-1.5 shadow-md hover:bg-red-50"
                  aria-label="Remove photo"
                >
                  <X className="h-5 w-5 text-red-500" />
                </button>
              </div>
              <img
                src={photoPreviewUrl}
                alt="Business card"
                className="w-full max-h-[200px] object-contain"
              />
            </div>
          )}

          <div className="relative">
            <textarea
              id="trade-info"
              value={inputText}
              onChange={(e) => {
                const newValue = e.target.value;
                setInputText(newValue);
                // Immediately call onInputChange to respond quickly to user input
                onInputChange?.(newValue.trim().length > 0);
              }}
              placeholder="Name, business, contact info..."
              className="w-full border border-gray-300 rounded-xl p-4 mb-4 min-h-[100px] resize-none focus:border-purple-500 focus:outline-none text-base bg-gray-50 focus:bg-white transition-colors duration-150"
              disabled={isProcessingImage}
            />
          </div>

          {/* Tip text - only show when not expanded and no input */}
          {!isExpanded && !inputText.trim() && (
            <p className="text-sm text-gray-600 text-center mb-4 transition-all duration-300">
              {isIOSDevice ? 'Try copying text from Photos app or Contacts' : 'Try business cards, vans or screenshot contacts from your address book'}
            </p>
          )}

          {/* Unified image capture button - only show when no photo is present */}
          {!photoPreviewUrl && !isIOSDevice && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 bg-purple-50 hover:bg-purple-100 active:bg-purple-200 text-purple-700 font-semibold py-4 px-4 rounded-xl transition-all duration-150"
                aria-label="Add photo"
                title="Add photo of business card or tradesperson"
                disabled={isProcessingImage}
              >
                <Camera className="h-5 w-5" />
                <span>Add from Photo</span>
              </button>
            </div>
          )}



          {/* Hidden file input - only on non-iOS devices */}
          {!isIOSDevice && <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            // Removed capture="environment" attribute as it can cause issues on iOS
            className="hidden"
            data-testid="photo-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleCapturePhoto(file);
              }
              // Reset the input value to allow selecting the same file again
              e.target.value = '';
            }}
          />}

          {/* Processing states */}
          {isParsing && !isProcessingImage && (
            <div className="text-sm text-purple-600 mb-2">
              <span className="inline-block animate-pulse">Detecting information...</span>
            </div>
          )}

          {isProcessingImage && (
            <div className="text-sm text-purple-600 flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <span>Processing image...</span>
              </div>
              {photoPreviewUrl && (
                <button
                  type="button"
                  onClick={() => setIsProcessingImage(false)}
                  className="ml-4 text-xs text-purple-700 underline"
                >
                  Skip processing
                </button>
              )}
            </div>
          )}
        </div>

        {/* Expanded form fields - only shown when user starts typing */}
        {isExpanded && (
          <div className="animate-in slide-in-from-bottom-4 duration-300">
            {/* Existing tradesperson notification */}
            {isExistingRecord && possibleMatch && displayMode === 'existingRecord' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-amber-100 p-1 rounded-full">
                    <Check className="h-5 w-5 text-amber-600" />
                  </div>
                  <p className="text-base text-amber-700 font-medium">
                    Hey! We found this tradesperson already in our database!
                  </p>
                </div>
                <p className="text-sm text-amber-600 mb-3">
                  Is this who you're looking for? You can use the existing record or create a new one.
                </p>
              </div>
            )}

            {/* Trade Card Design - Matches actual trade card dimensions */}
            <div className="p-3 border-2 border-purple-300 bg-white rounded-xl relative">
              {/* Top Row: Icon, Name and Trade Type - Matches trade card layout */}
              <div className="flex items-start mb-1">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    {editableFields.trade_type ? (
                      <TradeIcon tradeType={editableFields.trade_type} className="h-16 w-16" />
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center">
                        <span className="text-gray-400 text-xl">?</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Name field */}
                    <div className="flex items-center">
                      {parsedInfo.detectedFields.includes('name') && (
                        <div className="mr-2 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center animate-in fade-in-0 slide-in-from-left-2 duration-300">
                          <Check className="h-2.5 w-2.5 text-green-600" />
                        </div>
                      )}
                      <input
                        id="trade-name"
                        type="text"
                        value={editableFields.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        className="flex-1 font-bold text-lg text-purple-900 border-0 focus:outline-none bg-transparent leading-tight"
                        placeholder="Business Name"
                      />
                    </div>

                    {/* Trade Type field */}
                    <div className="flex items-center mt-0.5">
                      {parsedInfo.detectedFields.includes('trade_type') && (
                        <div className="mr-2 w-4 h-4 bg-green-100 rounded-full flex items-center justify-center animate-in fade-in-0 slide-in-from-left-2 duration-300">
                          <Check className="h-2 w-2 text-green-600" />
                        </div>
                      )}
                      <select
                        id="trade-type"
                        value={editableFields.trade_type}
                        onChange={(e) => updateField('trade_type', e.target.value)}
                        className="flex-1 text-base text-purple-600 border-0 focus:outline-none appearance-none cursor-pointer bg-transparent"
                      >
                        {tradeOptions.map(option => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={option.value.startsWith('group-')}
                            className={option.value.startsWith('group-') ? 'font-bold bg-purple-50' : ''}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact information section - Condensed */}
              <div className="space-y-2">
                {/* Phone field - Compact */}
                <div className="flex items-center pl-19">
                  {parsedInfo.detectedFields.includes('phone') && (
                    <div className="mr-2 w-4 h-4 bg-green-100 rounded-full flex items-center justify-center animate-in fade-in-0 slide-in-from-left-2 duration-300">
                      <Check className="h-2 w-2 text-green-600" />
                    </div>
                  )}
                  <Phone className="h-3 w-3 text-gray-400 mr-2 flex-shrink-0" />
                  <input
                    id="trade-phone"
                    type="tel"
                    value={editableFields.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="Phone number"
                    className="flex-1 py-1 px-0 text-base text-gray-900 border-0 focus:outline-none bg-transparent"
                  />
                </div>

                {/* Email field - Compact */}
                <div className="flex items-center pl-19">
                  {parsedInfo.detectedFields.includes('email') && (
                    <div className="mr-2 w-4 h-4 bg-green-100 rounded-full flex items-center justify-center animate-in fade-in-0 slide-in-from-left-2 duration-300">
                      <Check className="h-2 w-2 text-green-600" />
                    </div>
                  )}
                  <Mail className="h-3 w-3 text-gray-400 mr-2 flex-shrink-0" />
                  <input
                    id="trade-email"
                    type="email"
                    value={editableFields.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="Email address"
                    className="flex-1 py-1 px-0 text-base text-gray-900 border-0 focus:outline-none bg-transparent"
                  />
                </div>

                {/* Notes field - Compact */}
                <div className="flex items-start pl-19">
                  <MessageSquare className="h-3 w-3 text-gray-400 mr-2 flex-shrink-0 mt-1" />
                  <textarea
                    id="trade-notes"
                    value={editableFields.notes || ''}
                    onChange={(e) => updateField('notes', e.target.value)}
                    placeholder="Add private notes about this trade person"
                    className="flex-1 py-1 px-0 text-base text-gray-900 border-0 focus:outline-none bg-transparent resize-none min-h-[32px]"
                  />
                </div>
              </div>

            </div>

            {/* Sticky floating action button */}
            {(editableFields.name && editableFields.trade_type) && (
              <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
                <div className="max-w-md mx-auto pointer-events-auto">
                  {displayMode === 'existingRecord' && possibleMatch ? (
                    <div className="flex gap-3">
                      <Button
                        onClick={handleUseExisting}
                        disabled={isSubmitting}
                        className="flex-1 bg-orange-600/90 hover:bg-orange-600 active:bg-orange-700 text-white py-4 rounded-2xl text-base font-semibold backdrop-blur-sm transition-all duration-150"
                        style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)' }}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center">
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Adding...
                          </span>
                        ) : (
                          'Use Existing'
                        )}
                      </Button>
                      <Button
                        onClick={handleContinueWithNew}
                        disabled={isSubmitting}
                        variant="outline"
                        className="flex-1 border-2 border-gray-300 bg-white/90 text-gray-700 hover:bg-white active:bg-gray-50 py-4 rounded-2xl text-base font-semibold backdrop-blur-sm transition-all duration-150"
                      >
                        Create New
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleSave}
                      disabled={isSubmitting}
                      className="w-full bg-purple-600/90 hover:bg-purple-600 active:bg-purple-700 text-white py-4 rounded-2xl text-base font-semibold backdrop-blur-sm transition-all duration-150"
                      style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Adding Tradesperson...
                        </span>
                      ) : (
                        'Add Tradesperson'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Requirements message when fields are missing */}
            {(!editableFields.name || !editableFields.trade_type) && (
              <div className="mt-8 text-center">
                <p className="text-sm text-gray-500 flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Name and trade type are required
                </p>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  // Render based on display mode
  return (
    <div className="p-0 pb-32 transition-all duration-300">
      {displayMode === 'alreadySaved' ? renderAlreadySavedView() : renderFormView()}
    </div>
  );
}
